import { Component, Input, OnInit, Output, EventEmitter, signal, computed, effect, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import Swal from 'sweetalert2';
import { ToastrService } from 'ngx-toastr';
import { AmbassadorProgramCompanyVehicleScheduleService } from '../../../../services/ambassador-program-company-vehicle-schedule.service';
import { AmbassadorProgramCompanyVehicleScheduleDTO } from '../../../../models/ambassador-program-company-vehicle-schedule-dto';
import { AmbassadorProgramCompanyVehicleService } from '../../../../services/ambassador-program-company-vehicle.service';
import { AmbassadorProgramCompanyVehicleDTO } from '../../../../models/ambassador-program-company-vehicle-dto';
import { Router } from '@angular/router';
import { Select2Data, Select2 } from 'ng-select2-component';
import { Helpers } from '../../../../helpers/helpers';

type DayStatus = 'unavailable' | 'available' | 'occupied' | 'selected';

interface CalendarDay {
  date: Date;
  dayOftheWeek: string;
  day: number;
  status: DayStatus;
  isCurrentMonth: boolean;
  scheduledInit: boolean;
  scheduledEnd: boolean;
  hoverInSchedule: boolean;
  hoverDelayMs: number;
}

@Component({
  selector: 'ambassador-calendar',
  standalone: true,
  templateUrl: './ambassador-calendar.component.html',
  styleUrls: ['./ambassador-calendar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [CommonModule, Select2]
})
export class AmbassadorCalendarComponent implements OnInit {
  @Input() idVehicle: string = '';
  @Output() reservationRequested = new EventEmitter<{ idSchedule: string; timePreference: string }>();

  vehicle = signal<AmbassadorProgramCompanyVehicleDTO | null>(null);
  schedules = signal<AmbassadorProgramCompanyVehicleScheduleDTO[]>([]);
  currentDate = signal<Date>(new Date());
  selectedDays = signal<Date[]>([]);
  timePreference = signal<string>(''); // Tramos de una hora desde las 8:00 hasta las 13:00
  calendarInvalidSelection:boolean = false;
  timePreferenceInvalid:boolean = false;
  hoveredScheduleId = signal<string | null>(null);
  readonly hoverSequenceStepMs = 75;

  readonly months = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
  readonly weekDays = ['lun', 'mar', 'mié', 'jue', 'vie', 'sáb', 'dom'];
  timePreferences: Select2Data = [];

  currentMonthName = computed(() => {
    const date = this.currentDate();
    return this.months[date.getMonth()];
  });

  currentYear = computed(() => {
    return this.currentDate().getFullYear();
  });

  // Calcular meses disponibles basándose en los schedules con disponibilidad real
  availableMonths = computed(() => {
    const schedules = this.schedules();
    const months = new Set<string>();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    schedules.forEach(schedule => {
      if (!this.isScheduleSelectable(schedule, today)) {
        return;
      }
      const start = new Date(schedule.ScheduledInitDate);
      const end = new Date(schedule.ScheduledEndDate);

      // Agregar todos los meses entre start y end
      const current = new Date(start.getFullYear(), start.getMonth(), 1);
      const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);

      while (current <= endMonth) {
        const monthKey = `${current.getFullYear()}-${current.getMonth()}`;
        months.add(monthKey);
        current.setMonth(current.getMonth() + 1);
      }
    });

    return Array.from(months).sort().map(key => {
      const [year, month] = key.split('-').map(Number);
      return { year, month };
    });
  });

  // Verificar si el mes actual tiene días disponibles
  currentMonthHasAvailableDays = computed(() => {
    const current = this.currentDate();
    const available = this.availableMonths();
    const currentKey = `${current.getFullYear()}-${current.getMonth()}`;
    return available.some(m => `${m.year}-${m.month}` === currentKey);
  });

  hasAvailableDays = computed(() => {
    const schedules = this.schedules();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return schedules.some(schedule => this.isScheduleSelectable(schedule, today));
  });

  // Verificar si se puede navegar al mes anterior
  canNavigatePrevious = computed(() => {
    const current = this.currentDate();
    const available = this.availableMonths();
    const currentKey = `${current.getFullYear()}-${current.getMonth()}`;

    // Encontrar el mes disponible anterior al actual
    const currentIndex = available.findIndex(m => `${m.year}-${m.month}` === currentKey);
    return currentIndex > 0;
  });

  // Verificar si se puede navegar al mes siguiente
  canNavigateNext = computed(() => {
    const current = this.currentDate();
    const available = this.availableMonths();
    const currentKey = `${current.getFullYear()}-${current.getMonth()}`;

    // Encontrar el mes disponible siguiente al actual
    const currentIndex = available.findIndex(m => `${m.year}-${m.month}` === currentKey);
    return currentIndex >= 0 && currentIndex < available.length - 1;
  });

  calendarDays = computed(() => {
    const date = this.currentDate();
    const schedules = this.schedules();
    const selected = this.selectedDays();

    const year = date.getFullYear();
    const month = date.getMonth();

    // Primer día del mes
    const firstDay = new Date(year, month, 1);
    const firstDayOfWeek = (firstDay.getDay() + 6) % 7; // Lunes = 0

    // Último día del mes
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();

    const days: CalendarDay[] = [];

    // Días del mes anterior
    const prevMonth = new Date(year, month, 0);
    const daysInPrevMonth = prevMonth.getDate();
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const dayDate = new Date(year, month - 1, daysInPrevMonth - i);
      const status = this.getDayStatus(dayDate, schedules, selected);
      const scheduleFlags = this.getScheduleBoundaryFlags(dayDate, schedules, status);
      const hoverInfo = this.getHoverStateForDay(dayDate, schedules, this.hoveredScheduleId());
      days.push({
        date: dayDate,
        dayOftheWeek: this.weekDays[dayDate.getDay() - 1],
        day: daysInPrevMonth - i,
        status: status,
        isCurrentMonth: false,
        scheduledInit: scheduleFlags.scheduledInit,
        scheduledEnd: scheduleFlags.scheduledEnd,
        hoverInSchedule: hoverInfo.hoverInSchedule,
        hoverDelayMs: hoverInfo.hoverDelayMs
      });
    }

    // Días del mes actual
    for (let day = 1; day <= daysInMonth; day++) {
      const dayDate = new Date(year, month, day);
      const status = this.getDayStatus(dayDate, schedules, selected);
      const scheduleFlags = this.getScheduleBoundaryFlags(dayDate, schedules, status);
      const hoverInfo = this.getHoverStateForDay(dayDate, schedules, this.hoveredScheduleId());
      days.push({
        date: dayDate,
        dayOftheWeek: this.weekDays[dayDate.getDay() - 1],
        day: day,
        status: status,
        isCurrentMonth: true,
        scheduledInit: scheduleFlags.scheduledInit,
        scheduledEnd: scheduleFlags.scheduledEnd,
        hoverInSchedule: hoverInfo.hoverInSchedule,
        hoverDelayMs: hoverInfo.hoverDelayMs
      });
    }

    // Completar hasta 42 días (6 semanas)
    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      const dayDate = new Date(year, month + 1, day);
      const status = this.getDayStatus(dayDate, schedules, selected);
      const scheduleFlags = this.getScheduleBoundaryFlags(dayDate, schedules, status);
      const hoverInfo = this.getHoverStateForDay(dayDate, schedules, this.hoveredScheduleId());
      days.push({
        date: dayDate,
        dayOftheWeek: this.weekDays[dayDate.getDay() - 1],
        day: day,
        status: status,
        isCurrentMonth: false,
        scheduledInit: scheduleFlags.scheduledInit,
        scheduledEnd: scheduleFlags.scheduledEnd,
        hoverInSchedule: hoverInfo.hoverInSchedule,
        hoverDelayMs: hoverInfo.hoverDelayMs
      });
    }

    return days;
  });

  selectedDaysText = computed(() => {
    const selected = this.selectedDays();
    if (selected.length === 0) return '';

    const sorted = [...selected].sort((a, b) => a.getTime() - b.getTime());
    console.log(sorted);
    const first = sorted[0];
    const last = sorted[sorted.length - 1];

    const dayNames = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
    const monthNames = this.months;

    if (sorted.length === 1) {
      const dayName = dayNames[first.getDay()];
      const day = first.getDate();
      const month = monthNames[first.getMonth()];
      const year = first.getFullYear();
      return `${dayName}, ${day} de ${month} ${year}`;
    } else {
      const firstDayName = dayNames[first.getDay()];
      const firstDay = first.getDate();
      const firstMonth = monthNames[first.getMonth()];
      const firstYear = first.getFullYear();

      const lastPlus = new Date(last);
      lastPlus.setDate(lastPlus.getDate() + 1);
      const lastDayName = dayNames[lastPlus.getDay()];
      const lastDay = lastPlus.getDate();
      console.log(lastDay);
      const lastMonth = monthNames[lastPlus.getMonth()];
      const lastYear = lastPlus.getFullYear();

      // Si están en el mismo mes y año
      if (first.getMonth() === last.getMonth() && first.getFullYear() === last.getFullYear()) {
        return `del ${firstDayName} ${firstDay} al ${lastDayName} ${lastDay} de ${firstMonth} ${firstYear}`;
      } else {
        // Si están en meses o años diferentes
        return `del ${firstDayName} ${firstDay} de ${firstMonth} ${firstYear} al ${lastDayName} ${lastDay} de ${lastMonth} ${lastYear}`;
      }
    }
  });

  constructor(
    private vehicleScheduleService: AmbassadorProgramCompanyVehicleScheduleService,
    private vehicleService: AmbassadorProgramCompanyVehicleService,
    private router: Router,
    private toastr: ToastrService,
    private helper: Helpers
  ) {
    this.timePreferences = this.helper.timePreferences;
    // Efecto para inicializar al primer mes disponible cuando se cargan los schedules
    effect(() => {
      const schedules = this.schedules();
      const current = this.currentDate();

      if (schedules.length > 0) {
        const available = this.availableMonths();
        if (available.length > 0) {
          const currentKey = `${current.getFullYear()}-${current.getMonth()}`;
          const isCurrentAvailable = available.some(m => `${m.year}-${m.month}` === currentKey);

          // Solo cambiar si el mes actual no tiene días disponibles
          if (!isCurrentAvailable) {
            const firstMonth = available[0];
            const newDate = new Date(firstMonth.year, firstMonth.month, 1);
            this.currentDate.set(newDate);
          }
        }
      }
    });
  }

  ngOnInit() {
    this.loadVehicle();
    this.loadSchedules();
  }

  loadVehicle() {
    if (!this.idVehicle) return;

    this.vehicleService.get(this.idVehicle).subscribe(
      (resp) => {
        this.vehicle.set(resp);
      },
      (err) => {
        Swal.fire({
          icon: 'error',
          title: 'Error al recuperar los datos',
          text: 'Ocurrió un error al cargar la información del vehículo',
          customClass: {
            popup: 'custom-swal-popup',
            title: 'custom-swal-title',
            htmlContainer: 'custom-swal-html',
            cancelButton: 'custom-swal-cancel',
            confirmButton: 'custom-swal-confirm',
            icon: 'custom-swal-icon'
          },
          confirmButtonText: 'Cerrar <i class="fa-solid fa-xmark"></i>'
        });
      }
    );
  }

  loadSchedules() {
    if (!this.idVehicle) return;

    this.vehicleScheduleService.getAllByVehicle(this.idVehicle).subscribe(
      (resp) => {
        const schedules = resp.map(schedule => ({
          ...schedule,
          ScheduledInitDate: new Date(schedule.ScheduledInitDate),
          ScheduledEndDate: new Date(schedule.ScheduledEndDate)
        }));
        this.schedules.set(schedules);
      },
      (err) => {
        Swal.fire({
          icon: 'error',
          title: 'Error al recuperar los datos',
          text: 'Ocurrió un error al cargar los horarios',
          customClass: {
            popup: 'custom-swal-popup',
            title: 'custom-swal-title',
            htmlContainer: 'custom-swal-html',
            cancelButton: 'custom-swal-cancel',
            confirmButton: 'custom-swal-confirm',
            icon: 'custom-swal-icon'
          },
          confirmButtonText: 'Cerrar <i class="fa-solid fa-xmark"></i>'
        });
      }
    );
  }

  getDayStatus(date: Date, schedules: AmbassadorProgramCompanyVehicleScheduleDTO[], selected: Date[]): DayStatus {
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);

    // Verificar si es una fecha pasada
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (checkDate < today) return 'unavailable';

    // Buscar el schedule que contiene este día
    let scheduleForDay: AmbassadorProgramCompanyVehicleScheduleDTO | null = null;
    for (const schedule of schedules) {
      const start = new Date(schedule.ScheduledInitDate);
      const end = new Date(schedule.ScheduledEndDate);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);

      if (checkDate >= start && checkDate <= end) {
        scheduleForDay = schedule;
        break;
      }
    }

    // Si NO está en ningún rango de schedules, no está disponible
    if (!scheduleForDay) return 'unavailable';

    // Si el schedule tiene IsAvailable === false, el día está ocupado
    if (scheduleForDay.IsAvailable === false) {
      return 'occupied';
    }

    // Si el bloque no se ha solicitado y la fecha de inicio es hoy o pasada,
    // todos los días del bloque dejan de estar disponibles
    const scheduleStart = new Date(scheduleForDay.ScheduledInitDate);
    scheduleStart.setHours(0, 0, 0, 0);
    if (scheduleStart <= today) {
      return 'unavailable';
    }

    // Si está dentro de un rango de schedules y está disponible, verificar si está seleccionado
    const isSelected = selected.some(sel => {
      const selDate = new Date(sel);
      selDate.setHours(0, 0, 0, 0);
      return selDate.getTime() === checkDate.getTime();
    });
    if (isSelected) return 'selected';

    // Si está en un rango y está disponible, está disponible
    return 'available';
  }

  isScheduleSelectable(schedule: AmbassadorProgramCompanyVehicleScheduleDTO, today: Date): boolean {
    if (schedule.IsAvailable === false) return false;
    const start = new Date(schedule.ScheduledInitDate);
    start.setHours(0, 0, 0, 0);
    return start > today;
  }

  getScheduleBoundaryFlags(
    date: Date,
    schedules: AmbassadorProgramCompanyVehicleScheduleDTO[],
    status: DayStatus
  ): { scheduledInit: boolean; scheduledEnd: boolean } {
    if (status !== 'available') {
      return { scheduledInit: false, scheduledEnd: false };
    }

    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);

    for (const schedule of schedules) {
      if (schedule.IsAvailable === false) continue;
      const start = new Date(schedule.ScheduledInitDate);
      const end = new Date(schedule.ScheduledEndDate);
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);

      const isInit = checkDate.getTime() === start.getTime();
      const isEnd = checkDate.getTime() === end.getTime();
      if (isInit || isEnd) {
        return { scheduledInit: isInit, scheduledEnd: !isInit && isEnd };
      }
    }

    return { scheduledInit: false, scheduledEnd: false };
  }

  getHoverStateForDay(
    date: Date,
    schedules: AmbassadorProgramCompanyVehicleScheduleDTO[],
    hoveredScheduleId: string | null
  ): { hoverInSchedule: boolean; hoverDelayMs: number } {
    if (!hoveredScheduleId) {
      return { hoverInSchedule: false, hoverDelayMs: 0 };
    }

    const schedule = schedules.find(item => item.IdSchedule === hoveredScheduleId);
    if (!schedule || schedule.IsAvailable === false) {
      return { hoverInSchedule: false, hoverDelayMs: 0 };
    }

    const start = new Date(schedule.ScheduledInitDate);
    const end = new Date(schedule.ScheduledEndDate);
    const checkDate = new Date(date);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    checkDate.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (start <= today) {
      return { hoverInSchedule: false, hoverDelayMs: 0 };
    }

    if (checkDate < start || checkDate > end) {
      return { hoverInSchedule: false, hoverDelayMs: 0 };
    }

    const dayIndex = Math.floor((checkDate.getTime() - start.getTime()) / 86400000);
    return {
      hoverInSchedule: true,
      hoverDelayMs: dayIndex * this.hoverSequenceStepMs
    };
  }

  // Obtener el schedule que contiene un día específico
  getScheduleForDay(date: Date): AmbassadorProgramCompanyVehicleScheduleDTO | null {
    const schedules = this.schedules();
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);

    for (const schedule of schedules) {
      const start = new Date(schedule.ScheduledInitDate);
      const end = new Date(schedule.ScheduledEndDate);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);

      if (checkDate >= start && checkDate <= end) {
        return schedule;
      }
    }

    return null;
  }

  // Obtener el schedule que contiene los días seleccionados actualmente
  getScheduleForSelectedDays(selected: Date[]): AmbassadorProgramCompanyVehicleScheduleDTO | null {
    if (selected.length === 0) return null;

    const schedules = this.schedules();

    // Buscar el schedule que contiene todos los días seleccionados
    for (const schedule of schedules) {
      const start = new Date(schedule.ScheduledInitDate);
      const end = new Date(schedule.ScheduledEndDate);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);

      // Verificar si todos los días seleccionados están en este schedule
      const allInSchedule = selected.every(sel => {
        const selDate = new Date(sel);
        selDate.setHours(0, 0, 0, 0);
        return selDate >= start && selDate <= end;
      });

      if (allInSchedule) {
        return schedule;
      }
    }

    return null;
  }

  // Verificar si dos schedules son el mismo
  isSameSchedule(schedule1: AmbassadorProgramCompanyVehicleScheduleDTO | null, schedule2: AmbassadorProgramCompanyVehicleScheduleDTO | null): boolean {
    if (!schedule1 || !schedule2) return false;
    return schedule1.IdSchedule === schedule2.IdSchedule;
  }

  // Obtener todos los días dentro de un rango de schedule
  getDaysInScheduleRange(schedule: AmbassadorProgramCompanyVehicleScheduleDTO): Date[] {
    const days: Date[] = [];
    const start = new Date(schedule.ScheduledInitDate);
    const end = new Date(schedule.ScheduledEndDate);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    const current = new Date(start);
    while (current <= end) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return days;
  }

  // Verificar si todos los días de un rango están seleccionados
  areAllDaysInRangeSelected(rangeDays: Date[], selected: Date[]): boolean {
    return rangeDays.every(rangeDay => {
      const rangeDayKey = `${rangeDay.getFullYear()}-${rangeDay.getMonth()}-${rangeDay.getDate()}`;
      return selected.some(sel => {
        const selKey = `${sel.getFullYear()}-${sel.getMonth()}-${sel.getDate()}`;
        return selKey === rangeDayKey;
      });
    });
  }

  onDayClick(day: CalendarDay) {
    this.calendarInvalidSelection = false;
    // No permitir seleccionar días no disponibles u ocupados
    if (day.status === 'unavailable' || day.status === 'occupied') {
      return;
    }

    const selected = this.selectedDays();
    const clickedDate = new Date(day.date);
    clickedDate.setHours(0, 0, 0, 0);

    // Obtener el schedule que contiene este día
    const schedule = this.getScheduleForDay(clickedDate);

    // Si hay un schedule pero está ocupado (IsAvailable === false), no permitir selección
    if (schedule && schedule.IsAvailable === false) {
      return;
    }

    if (!schedule) {
      // Si no hay schedule, mantener el comportamiento anterior
      const index = selected.findIndex(sel =>
        sel.getDate() === clickedDate.getDate() &&
        sel.getMonth() === clickedDate.getMonth() &&
        sel.getFullYear() === clickedDate.getFullYear()
      );

      if (index !== -1) {
        selected.splice(index, 1);
        this.selectedDays.set([...selected]);
      } else {
        this.selectedDays.set([clickedDate]);
      }
      return;
    }

    // Obtener el schedule de los días actualmente seleccionados
    const currentSelectedSchedule = this.getScheduleForSelectedDays(selected);

    // Obtener todos los días del rango del schedule clickeado
    const rangeDays = this.getDaysInScheduleRange(schedule);

    // Si el día clickeado está en un schedule diferente al seleccionado, reemplazar
    if (currentSelectedSchedule && !this.isSameSchedule(currentSelectedSchedule, schedule)) {
      // Deseleccionar todo y seleccionar el nuevo rango completo
      this.selectedDays.set(rangeDays.map(d => new Date(d)));
      return;
    }

    // Si es el mismo schedule o no hay nada seleccionado, mantener el comportamiento de toggle
    // Verificar si todos los días del rango ya están seleccionados
    if (this.areAllDaysInRangeSelected(rangeDays, selected)) {
      // Si todos están seleccionados, deseleccionar todo el rango
      this.selectedDays.set([]);
    } else {
      // Si no todos están seleccionados, seleccionar todo el rango
      this.selectedDays.set(rangeDays.map(d => new Date(d)));
    }
  }

  onDayHover(day: CalendarDay) {
    const schedule = this.getScheduleForDay(day.date);
    if (!schedule || schedule.IsAvailable === false) {
      this.hoveredScheduleId.set(null);
      return;
    }

    const start = new Date(schedule.ScheduledInitDate);
    start.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (start <= today) {
      this.hoveredScheduleId.set(null);
      return;
    }

    this.hoveredScheduleId.set(schedule.IdSchedule);
  }

  onDayHoverEnd() {
    this.hoveredScheduleId.set(null);
  }

  previousMonth() {
    if (!this.canNavigatePrevious()) return;

    const current = this.currentDate();
    const available = this.availableMonths();
    const currentKey = `${current.getFullYear()}-${current.getMonth()}`;
    const currentIndex = available.findIndex(m => `${m.year}-${m.month}` === currentKey);

    if (currentIndex > 0) {
      const prevMonth = available[currentIndex - 1];
      const newDate = new Date(prevMonth.year, prevMonth.month, 1);
      this.currentDate.set(newDate);
    }
  }

  nextMonth() {
    if (!this.canNavigateNext()) return;

    const current = this.currentDate();
    const available = this.availableMonths();
    const currentKey = `${current.getFullYear()}-${current.getMonth()}`;
    const currentIndex = available.findIndex(m => `${m.year}-${m.month}` === currentKey);

    if (currentIndex >= 0 && currentIndex < available.length - 1) {
      const nextMonth = available[currentIndex + 1];
      const newDate = new Date(nextMonth.year, nextMonth.month, 1);
      this.currentDate.set(newDate);
    }
  }

  onRequest() {
    this.calendarInvalidSelection = false;
    this.timePreferenceInvalid = false;
    const selected = this.selectedDays();
    const schedule = this.getScheduleForSelectedDays(selected);
    const timePref = this.timePreference();

    if (schedule) {
      // Verificar que el schedule esté disponible
      if (schedule.IsAvailable === false) {
        this.toastr.error('El rango de fechas seleccionado no está disponible');
        this.calendarInvalidSelection = true;
        // Swal.fire({
        //   icon: 'error',
        //   title: 'Error',
        //   text: 'El rango de fechas seleccionado no está disponible',
        // customClass: {
        //   popup: 'custom-swal-popup',
        //   title: 'custom-swal-title',
        //   htmlContainer: 'custom-swal-html',
        //   cancelButton: 'custom-swal-cancel',
        //   confirmButton: 'custom-swal-confirm',
        //   icon: 'custom-swal-icon'
        // },
        // confirmButtonText: 'Cerrar <i class="fa-solid fa-xmark"></i>'
        // });
        return;
      }

      if (timePref) {
        // Emitir evento al componente padre
        this.reservationRequested.emit({
          idSchedule: schedule.IdSchedule,
          timePreference: timePref
        });
      }
      else {
        this.toastr.error('No hay ninguna preferencia de hora seleccionada');
        this.timePreferenceInvalid = true;
        // Swal.fire({
        //   icon: 'error',
        //   title: 'Error',
        //   text: 'No hay ninguna preferencia de hora seleccionada',
        // customClass: {
        //   popup: 'custom-swal-popup',
        //   title: 'custom-swal-title',
        //   htmlContainer: 'custom-swal-html',
        //   cancelButton: 'custom-swal-cancel',
        //   confirmButton: 'custom-swal-confirm',
        //   icon: 'custom-swal-icon'
        // },
        // confirmButtonText: 'Cerrar <i class="fa-solid fa-xmark"></i>'
        // });
        return;
      }
    } else {
      this.toastr.error('No hay ningún rango de fechas seleccionado');
      this.calendarInvalidSelection = true;
      // Swal.fire({
      //   icon: 'error',
      //   title: 'Error',
      //   text: 'No hay ningún rango de fechas seleccionado',
      // customClass: {
      //   popup: 'custom-swal-popup',
      //   title: 'custom-swal-title',
      //   htmlContainer: 'custom-swal-html',
      //   cancelButton: 'custom-swal-cancel',
      //   confirmButton: 'custom-swal-confirm',
      //   icon: 'custom-swal-icon'
      // },
      // confirmButtonText: 'Cerrar <i class="fa-solid fa-xmark"></i>'
      // });
      return;
    }
  }

  onBack() {
    this.router.navigate(['/ambassador-home']);
  }
}

